import { useState, useEffect } from 'react'
import { X, PencilLine } from 'lucide-react'
import { clsx } from 'clsx'
import { Card, Badge, Button, Input, Select, Divider, Modal } from '../../../shared/components/UI'
import { machinesApi, unitsApi } from '../../../shared/lib/api'

const emptyMachine = { name: '', unitId: '', idealLoadTimeMin: 45, status: 'active', type: 'spindle', bladeCount: '', maxWidthMm: '', minSlitWidthMm: '' }
const emptyPosition = { name: '', spindleCount: 75, status: 'active' }

export default function MachinesPage() {
  const [machines, setMachines] = useState([])
  const [units, setUnits] = useState([])
  const [loading, setLoading] = useState(true)
  const [unitFilter, setUnitFilter] = useState('all')
  const [selectedMachine, setSelectedMachine] = useState(null)
  const [machineForm, setMachineForm] = useState(null)
  const [mFormData, setMFormData] = useState(emptyMachine)
  const [posForm, setPosForm] = useState(null)
  const [pFormData, setPFormData] = useState(emptyPosition)

  useEffect(() => {
    Promise.all([
      machinesApi.list(),
      unitsApi.list()
    ]).then(([mRes, uRes]) => {
      setMachines(mRes.data.data)
      const uData = uRes.data.data
      setUnits(uData)
      if (uData.length > 0) {
        setMFormData((p) => ({ ...p, unitId: uData[0].id }))
      }
      // Select first machine by default
      if (mRes.data.data.length > 0) {
        setSelectedMachine(mRes.data.data[0])
      }
    }).finally(() => setLoading(false))
  }, [])

  const mf = (k) => (e) => setMFormData((p) => ({ ...p, [k]: e.target.value }))
  const pf = (k) => (e) => setPFormData((p) => ({ ...p, [k]: e.target.value }))

  const visibleMachines = machines.filter((m) =>
    unitFilter === 'all' || m.unit_id === unitFilter
  )

  const refreshMachines = () => {
    machinesApi.list().then((res) => {
      const data = res.data.data
      setMachines(data)
      if (selectedMachine) {
        const updated = data.find((m) => m.id === selectedMachine.id)
        if (updated) setSelectedMachine(updated)
      }
    })
  }

  const saveMachine = () => {
    if (!mFormData.name.trim()) return
    const payload = {
      name: mFormData.name.trim(),
      unitId: mFormData.unitId,
      idealLoadTimeMin: parseInt(mFormData.idealLoadTimeMin || 0),
      status: mFormData.status,
      type: mFormData.type,
      bladeCount: mFormData.type === 'slitter' ? parseInt(mFormData.bladeCount) : null,
      maxWidthMm: mFormData.type === 'slitter' ? parseInt(mFormData.maxWidthMm) : null,
      minSlitWidthMm: mFormData.type === 'slitter' ? parseFloat(mFormData.minSlitWidthMm) : null,
    }

    const request = machineForm === 'new'
      ? machinesApi.create(payload)
      : machinesApi.update(machineForm, payload)

    request.then(() => {
      refreshMachines()
      setMachineForm(null)
    })
  }

  const savePosition = () => {
    if (!pFormData.name.trim() || !selectedMachine) return
    const payload = {
      name: pFormData.name.trim(),
      spindleCount: parseInt(pFormData.spindleCount),
      status: pFormData.status
    }

    const request = posForm === 'new'
      ? machinesApi.addPosition(selectedMachine.id, payload)
      : machinesApi.updatePosition(selectedMachine.id, posForm, payload)

    request.then(() => {
      refreshMachines()
      setPosForm(null)
    })
  }

  const togglePositionStatus = (machineId, posId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active'
    machinesApi.updatePosition(machineId, posId, { status: newStatus }).then(() => {
      refreshMachines()
    })
  }

  const totalPositions = (m) => (m.positions || []).filter((p) => p.status === 'active').length
  const totalSpindles = (m) => (m.positions || []).filter((p) => p.status === 'active').reduce((s, p) => s + (p.spindle_count || 0), 0)

  if (loading) return <div className="p-8 text-center text-gray-400">Loading machines...</div>

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-t-4 border-gray-100 border-t-brand-600 rounded-xl p-4">
          <p className="stat-label">Total Machines</p>
          <p className="stat-val">{machines.length}</p>
        </div>
        <div className="bg-white border border-t-4 border-gray-100 border-t-teal-500 rounded-xl p-4">
          <p className="stat-label">Total Positions</p>
          <p className="stat-val">{machines.reduce((s, m) => s + totalPositions(m), 0)}</p>
        </div>
        <div className="bg-white border border-t-4 border-gray-100 border-t-amber-500 rounded-xl p-4">
          <p className="stat-label">Total Spindles</p>
          <p className="stat-val">{machines.reduce((s, m) => s + totalSpindles(m), 0)}</p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <select
                className="form-input flex-1 text-sm"
                value={unitFilter}
                onChange={(e) => setUnitFilter(e.target.value)}
              >
                <option value="all">All Units</option>
                {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <Button onClick={() => { setMachineForm('new'); setMFormData({ ...emptyMachine, unitId: units[0]?.id || '' }) }}>+ Machine</Button>
            </div>

            {visibleMachines.map((m) => (
              <div
                key={m.id}
                onClick={() => { setSelectedMachine(m); setMachineForm(null); setPosForm(null) }}
                className={clsx(
                  'relative border rounded-3xl p-5 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-xl overflow-hidden group',
                  m.type === 'slitter' 
                    ? 'bg-gradient-to-br from-[#f8f9ff] to-[#f3f4ff] border-indigo-100' 
                    : 'bg-white border-gray-100 hover:border-brand-300',
                  selectedMachine?.id === m.id 
                    ? (m.type === 'slitter' ? 'border-indigo-400 ring-4 ring-indigo-50 shadow-indigo-100' : 'border-brand-500 ring-4 ring-brand-50 shadow-brand-100') 
                    : 'shadow-sm'
                )}
              >
                {/* Decorative background element for slitter */}
                {m.type === 'slitter' && (
                   <div className="absolute -right-6 -top-6 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl pointer-events-none transition-transform group-hover:scale-150 duration-700" />
                )}
                
                {m.type === 'slitter' && (
                  <div className="absolute top-0 right-0 rounded-bl-2xl rounded-tr-2xl px-4 py-1.5 bg-indigo-500 text-[10px] font-black text-white uppercase tracking-widest shadow-md">
                    Slitter
                  </div>
                )}
                
                <div className="flex justify-between items-start mb-4 relative z-10">
                  <div className="flex items-center gap-3">
                    <div className={clsx(
                      "w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm border",
                      m.type === 'slitter' ? "bg-white border-indigo-100 text-indigo-500" : "bg-brand-50 border-brand-100 text-brand-600"
                    )}>
                      {m.type === 'slitter' ? <span className="text-xl">✂️</span> : <span className="text-xl">⚙️</span>}
                    </div>
                    <div>
                      <h3 className="text-base font-black text-gray-900 tracking-tight leading-none mb-1.5">{m.name}</h3>
                      <div className="flex items-center gap-2">
                         <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{m.unit?.name || '-'}</span>
                         <div className="w-1 h-1 rounded-full bg-gray-300" />
                         <span className={clsx(
                           "text-[10px] font-black uppercase tracking-wider",
                           m.status === 'active' ? "text-emerald-500" : "text-gray-400"
                         )}>
                           {m.status === 'active' ? 'Active' : 'Inactive'}
                         </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setMachineForm(m.id)
                        setMFormData({
                          name: m.name,
                          unitId: m.unit_id,
                          idealLoadTimeMin: m.ideal_load_time_min,
                          status: m.status,
                          type: m.type || 'spindle',
                          bladeCount: m.blade_count || '',
                          maxWidthMm: m.max_width_mm || '',
                          minSlitWidthMm: m.min_slit_width_mm || ''
                        })
                      }}
                      className="w-8 h-8 flex items-center justify-center bg-white border border-gray-100 text-gray-400 hover:text-brand-600 hover:border-brand-200 rounded-xl transition-all shadow-sm z-20 relative"
                      title="Edit Machine"
                    >
                      <PencilLine size={14} />
                    </button>
                  </div>
                </div>

                <div className="relative z-10">
                  {m.type === 'slitter' ? (
                    <div className="grid grid-cols-3 gap-3 bg-white/60 p-3 rounded-2xl border border-white">
                      {[
                        { label: 'Blades', value: m.blade_count || '-' },
                        { label: 'Max Width', value: m.max_width_mm ? `${m.max_width_mm}mm` : '-' },
                        { label: 'Min Slit', value: m.min_slit_width_mm ? `${m.min_slit_width_mm}mm` : '-' },
                      ].map(({ label, value }) => (
                        <div key={label} className="text-center">
                          <p className="text-[9px] font-bold text-indigo-400/80 uppercase tracking-widest mb-0.5">{label}</p>
                          <p className="text-[13px] font-black text-indigo-900">{value}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                         <div className="bg-gray-50/80 rounded-2xl p-3 border border-gray-100 flex items-center justify-between">
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Total Spindles</span>
                            <span className="text-sm font-black text-gray-800">{totalSpindles(m)}</span>
                         </div>
                         <div className="bg-gray-50/80 rounded-2xl p-3 border border-gray-100 flex items-center justify-between">
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Ideal Time</span>
                            <span className="text-sm font-black text-gray-800">{m.ideal_load_time_min}m</span>
                         </div>
                      </div>
                      
                      {/* Visual Positions Preview */}
                      <div className="pt-2 flex items-center gap-2 overflow-x-auto no-scrollbar">
                         <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap">Positions:</span>
                         {m.positions?.filter(p => p.status === 'active').length > 0 ? (
                            m.positions.filter(p => p.status === 'active').slice(0, 3).map(p => (
                               <div key={p.id} className="px-2.5 py-1 bg-brand-50 border border-brand-100 rounded-lg whitespace-nowrap">
                                  <span className="text-[10px] font-bold text-brand-700">{p.name} <span className="text-brand-400 ml-1">({p.spindle_count})</span></span>
                               </div>
                            ))
                         ) : (
                            <span className="text-[10px] font-bold text-gray-300 italic">No active positions</span>
                         )}
                         {m.positions?.filter(p => p.status === 'active').length > 3 && (
                            <div className="px-2 py-1 bg-gray-50 border border-gray-100 text-gray-500 text-[10px] font-bold rounded-lg">
                               +{m.positions.filter(p => p.status === 'active').length - 3} more
                            </div>
                         )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {visibleMachines.length === 0 && (
              <div className="text-center py-8 text-gray-400 text-sm">No machines found</div>
            )}
          </div>

          <div>
            {selectedMachine && (
              <Card
                title={`${selectedMachine.name} • ${selectedMachine.type === 'slitter' ? 'Slitter Details' : 'Positions'}`}
                action={selectedMachine.type === 'spindle' && <Button size="sm" onClick={() => { setPosForm('new'); setPFormData(emptyPosition) }}>+ Position</Button>}
                bodyClass="!p-0"
              >
                {selectedMachine.type === 'slitter' ? (
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-3 gap-6">
                      <div className="bg-gray-50 rounded-2xl p-4 text-center border border-gray-100">
                        <p className="text-xs text-gray-400 mb-1">Blade Count</p>
                        <p className="text-2xl font-bold text-gray-800">{selectedMachine.blade_count || '-'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-4 text-center border border-gray-100">
                        <p className="text-xs text-gray-400 mb-1">Max Roll Width</p>
                        <p className="text-2xl font-bold text-gray-800">{selectedMachine.max_width_mm ? `${selectedMachine.max_width_mm}mm` : '-'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-4 text-center border border-gray-100">
                        <p className="text-xs text-gray-400 mb-1">Min Slit Width</p>
                        <p className="text-2xl font-bold text-gray-800">{selectedMachine.min_slit_width_mm ? `${selectedMachine.min_slit_width_mm}mm` : '-'}</p>
                      </div>
                    </div>

                    <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl text-xs text-blue-700 leading-relaxed">
                      <strong>About Slitter Machines:</strong> These machines are used to cut large parent rolls into thinner sub-rolls or strips. The blade count and width specifications help the supervisor plan the cutting sessions efficiently.
                    </div>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Position</th>
                        <th>Spindles</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(!selectedMachine.positions || selectedMachine.positions.length === 0) && (
                        <tr><td colSpan={4} className="text-center py-8 text-gray-400 text-sm">No positions yet</td></tr>
                      )}
                      {(selectedMachine.positions || []).map((p) => (
                        <tr key={p.id} className={p.status === 'inactive' ? 'opacity-40' : ''}>
                          <td className="font-medium">{p.name}</td>
                          <td>
                            <span className="inline-flex items-center gap-1 justify-start w-full">
                              <span className="text-brand-600 font-semibold">{p.spindle_count}</span>
                              <span className="text-xs text-gray-400">spindles</span>
                            </span>
                          </td>
                          <td>
                            <div className="flex justify-start">
                              <button
                                onClick={() => togglePositionStatus(selectedMachine.id, p.id, p.status)}
                                className={`text-xs font-medium px-2.5 py-0.5 rounded-full transition-colors ${p.status === 'active' ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                              >
                                {p.status === 'active' ? 'Active' : 'Inactive'}
                              </button>
                            </div>
                          </td>
                          <td className="pr-4">
                            <div className="flex justify-start items-center">
                              <button
                                onClick={() => { setPosForm(p.id); setPFormData({ name: p.name, spindleCount: p.spindle_count, status: p.status }) }}
                                className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition-all inline-flex"
                                title="Edit Position"
                              >
                                <PencilLine size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {selectedMachine.type === 'spindle' && (
                  <div className="px-4 py-3 bg-brand-50 border-t border-brand-100 text-xs text-brand-700">
                    Ideal load time: <strong>{selectedMachine.ideal_load_time_min} minutes</strong> per position
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>
      </div>

      {machineForm && (
        <Modal
          isOpen={!!machineForm}
          onClose={() => setMachineForm(null)}
          title={machineForm === 'new' ? 'Configure New Machine' : 'Edit Machine Profile'}
          size="md"
          confirmClose={mFormData.name.trim() !== ''}
        >
          <div className="space-y-5">
            <Select
              label="Production Unit"
              value={mFormData.unitId}
              onChange={mf('unitId')}
              options={units.map((u) => ({ label: u.name, value: u.id }))}
              className="rounded-xl"
            />
            <Input label="Machine Name" value={mFormData.name} onChange={mf('name')} placeholder="e.g. Slitter X1" className="rounded-xl" />
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Machine Type"
                value={mFormData.type}
                onChange={mf('type')}
                options={[{ value: 'spindle', label: 'Spindle (Loading)' }, { value: 'slitter', label: 'Slitter (Cutting)' }]}
                className="rounded-xl"
              />
              <Select
                label="Current Status"
                value={mFormData.status}
                onChange={mf('status')}
                options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
                className="rounded-xl"
              />
            </div>

            {mFormData.type === 'spindle' ? (
              <div className="space-y-4">
                <Input
                  label="Ideal Load Time (Min)"
                  value={mFormData.idealLoadTimeMin}
                  onChange={mf('idealLoadTimeMin')}
                  type="number"
                  min="1"
                  placeholder="e.g. 45"
                  className="rounded-xl"
                />
                <div className="p-4 bg-gray-50 border border-gray-100 rounded-2xl text-[11px] text-gray-500 leading-relaxed">
                  <strong>Tip:</strong> Ideal load time is the expected duration to complete one loading cycle per position.
                </div>
              </div>
            ) : (
              <div className="p-5 bg-indigo-50 border border-indigo-100 rounded-2xl text-[12px] text-indigo-700 leading-relaxed shadow-inner">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-indigo-600 text-white rounded-xl flex items-center justify-center font-bold">✂️</div>
                  <strong className="text-sm font-black">Slitter Setup</strong>
                </div>
                For now, you only need to provide a name for this Slitter machine. Detailed specifications like blade count and width limits can be configured later.
              </div>
            )}

            <div className="pt-4">
              <Button onClick={saveMachine} disabled={!mFormData.name.trim()} className="w-full h-14 rounded-2xl font-bold shadow-lg shadow-brand-100 bg-brand-600 text-white">
                {machineForm === 'new' ? 'Create Machine' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {posForm && selectedMachine && (
        <Modal
          isOpen={!!posForm}
          onClose={() => setPosForm(null)}
          title={posForm === 'new' ? `Add Position to ${selectedMachine.name}` : 'Edit Position Details'}
          size="md"
          confirmClose={pFormData.name.trim() !== ''}
        >
          <div className="space-y-5">
            <Input label="Position Name" value={pFormData.name} onChange={pf('name')} placeholder="e.g. Position A" className="rounded-xl" />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Number of Spindles"
                value={pFormData.spindleCount}
                onChange={pf('spindleCount')}
                type="number"
                min="1"
                placeholder="e.g. 4"
                className="rounded-xl"
              />
              <Select
                label="Current Status"
                value={pFormData.status}
                onChange={pf('status')}
                options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
                className="rounded-xl"
              />
            </div>

            <div className="p-4 bg-teal-50 border border-teal-100 rounded-2xl text-[11px] text-teal-700 leading-relaxed">
              One position represents one loading assignment. The supervisor will select this position when starting a new load.
            </div>

            <div className="pt-4">
              <Button onClick={savePosition} disabled={!pFormData.name.trim()} className="w-full h-14 rounded-2xl font-bold shadow-lg shadow-teal-100/50 !bg-teal-600 text-white">
                {posForm === 'new' ? 'Create Position' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
